/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useContext, useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

import {Box, Container, Stack, Heading} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {useContentAsset} from '../../hooks/use-content-asset'
import {SegmentContext} from '../../integrations/segment/app/contexts'

import LinksList from '../links-list'
import MenuCategoryImages from './menu-category-images'
import {mainMenuItemSelected} from '../../integrations/segment/app/page/home'
import {ChevronRightIcon} from '../icons'
import {categoryUrlBuilder} from '../../utils/url'
import {getListMenuCategories} from '../../utils/custom-utils'

const ListMenuContent = ({item, itemsKey, onClose, initialFocusRef}) => {
    const segmentContext = useContext(SegmentContext)
    const {locale} = useIntl()
    const items = item?.[itemsKey] || []

    const headerMenuItemAssets = useMemo(() => {
        return item?.c_headerMenuItemAssets
    }, [item?.c_headerMenuItemAssets, items])

    const headerMenuContentAssets = useContentAsset(headerMenuItemAssets, true)
    const headerMenuContentCategories = useMemo(() => {
        return getListMenuCategories(headerMenuContentAssets)
    }, [headerMenuContentAssets])
    const [hoveredIndex, setHoveredIndex] = useState(null)

    const handleMouseOver = (index) => {
        setHoveredIndex(index)
    }

    const handleMouseLeave = () => {
        setHoveredIndex(null)
    }

    return (
        <Container as={Stack} p="0">
            <Box className="container-flex">
                <Box className="level-2-menu-block">
                    <Box className="level-2-heading">
                        <Heading as="h3" className="level-2-heading-text">
                            {item?.name}
                        </Heading>
                        <Link
                            to={categoryUrlBuilder(item, locale)}
                            onClick={() => {
                                onClose()
                                mainMenuItemSelected(item, segmentContext)
                            }}
                            className="level-2-heading-link"
                            style={{padding: '0'}}
                        >
                            Shop All
                            <ChevronRightIcon />
                        </Link>
                    </Box>
                    <Box className="level-2-link-block" minH={'250px'} m={-2}>
                        {items?.map((item, index) => {
                            const {id, name} = item
                            const items = item[itemsKey] || []
                            const alternateURL = item?.c_alternativeUrl?.split('/collections/')[1]
                            const categoryId = alternateURL
                                ? {id: alternateURL?.replace(/\/$/, '')}
                                : item
                            const heading = {
                                href: categoryUrlBuilder(categoryId, locale),
                                text: name,
                                styles: {
                                    fontSize: 'md',
                                    marginBottom: 2
                                }
                            }

                            const links = items

                            return (
                                <LinksList
                                    key={id}
                                    heading={heading}
                                    links={links}
                                    color={'gray.900'}
                                    onLinkClick={() => {
                                        onClose()
                                        mainMenuItemSelected(item, segmentContext)
                                    }}
                                    {...(index === 0 ? {headingLinkRef: initialFocusRef} : {})}
                                />
                            )
                        })}
                    </Box>
                </Box>
                <MenuCategoryImages
                    headerMenuContentCategories={headerMenuContentCategories}
                    isLoading={false}
                    locale={locale}
                    parentCategory={item}
                    categoryUrlBuilder={categoryUrlBuilder}
                    onClose={onClose}
                    handleMouseOver={handleMouseOver}
                    handleMouseLeave={handleMouseLeave}
                    hoveredIndex={hoveredIndex}
                />
            </Box>
        </Container>
    )
}

ListMenuContent.propTypes = {
    item: PropTypes.object,
    itemsKey: PropTypes.string,
    maxColumns: PropTypes.number,
    onClose: PropTypes.func,
    initialFocusRef: PropTypes.object
}

export {ListMenuContent}
